export type PerformanceStatus =
  | 'event_listing_loaded'
  | 'rules_set'
  | 'test_config'
  | 'client_confirmed'
  | 'tm1_entry_configured'
  | 'testing'
  | 'event_day_ready'
  | 'ready';

export interface PerformanceStatusHistoryEntry {
  time: number;
  status: PerformanceStatus;
  username: string;
}

export interface PerformanceAuditChange {
  old: unknown;
  new: unknown;
}

export interface PerformanceAuditTestPayload {
  barcode?: string;
  item?: string | number;
  key?: string;
}

export interface PerformanceAuditTrailEntry {
  time: number;
  username: string;
  action: 'create' | 'update' | 'test_added';
  changes?: Record<string, PerformanceAuditChange>;
  test?: PerformanceAuditTestPayload;
}

export interface PerformanceExpectedChange {
  time: number;
  summary: string;
  entries: string[];
}

// How a ticket may be used across event days. 'one_time' = single-day one-time
// use (the default); 'multi_day' = valid across multiple event days; 'either'
// where the ticket can be issued as either (e.g. F1 "One-time or Multi-day").
export type TicketUsage = 'one_time' | 'multi_day' | 'either';

// Access-control metadata captured per event code. Mirrors the "Ticket Type &
// Message" and "One-time or Multi-day" columns of the venue access-control
// spreadsheets. `message` is shown on the handheld scanner and is capped at
// SCANNER_MESSAGE_MAX_LENGTH characters (JANAM display limit).
export interface EventScanMeta {
  message?: string;
  usage?: TicketUsage;
}

// Maximum length of a scanner message. JANAM handhelds display up to 16 chars.
export const SCANNER_MESSAGE_MAX_LENGTH = 16;

export interface Performance {
  key: string;
  item: number;
  description: string;
  status: PerformanceStatus;
  hiddenEvents?: string[];
  customArchticsEvents?: string[];
  customHostEvents?: string[];
  // Per-event access-control metadata, keyed by event code.
  eventScanMeta?: Record<string, EventScanMeta>;
  lastExpectedChange?: PerformanceExpectedChange;
  status_history?: PerformanceStatusHistoryEntry[];
  auditTrail?: PerformanceAuditTrailEntry[];
  ttl?: number;
}

export interface PerformanceCreateRequest {
  venueID: string;
  timestamp: number;
  description?: string;
  status?: PerformanceStatus;
  hiddenEvents?: string[];
  customArchticsEvents?: string[];
  customHostEvents?: string[];
  eventScanMeta?: Record<string, EventScanMeta>;
  lastExpectedChange?: PerformanceExpectedChange;
}
